import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const STATE_STYLES = {
  active: "border-auroraDeep bg-auroraDeep text-paper",
  research: "border-ink bg-paper text-ink",
  concept: "border-line bg-paper text-stone",
  future: "border-dashed border-line bg-transparent text-stone/70",
};

export default function Vision() {
  const { t } = useLanguage();
  const v = t.vision;

  return (
    <section id="vision" className="py-24 md:py-32 lg:py-40 border-b border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-auroraDeep mb-6">{v.label}</p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            {v.titlePrefix}<span className="font-serif-accent italic font-medium">{v.titleHighlight}</span>{v.titleSuffix}
          </h2>
          <p className="mt-8 max-w-2xl text-lg font-light text-stone leading-relaxed">
            {v.description}
          </p>
        </Reveal>

        <div className="mt-20 grid lg:grid-cols-3 gap-10 items-center">
          {/* Orchestrator */}
          <Reveal>
            <div className="relative aspect-square max-w-sm mx-auto flex items-center justify-center border border-line rounded-full">
              <div className="absolute inset-8 border border-line rounded-full" />
              <div className="absolute inset-16 border border-dashed border-line rounded-full animate-[spin_40s_linear_infinite]" />
              <div className="relative z-10 text-center">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-auroraDeep mb-2">{v.centerLabel}</p>
                <p className="font-display font-extrabold text-2xl md:text-3xl leading-tight max-w-[9rem] mx-auto">
                  {v.orchestratorName}
                </p>
              </div>
            </div>
          </Reveal>

          {/* Modules */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {v.modules.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.06 }}
                className={`border p-5 min-h-[130px] flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300 ${STATE_STYLES[m.state]}`}
                data-testid={`vision-module-${i}`}
              >
                <p className="font-display font-medium text-lg leading-snug">{m.name}</p>
                <p className={`font-mono-tech text-[10px] uppercase tracking-[0.15em] ${m.state === "active" ? "text-paper/80" : "text-current opacity-70"}`}>
                  {m.state === "active" ? "" : `${v.comingSoon} `}{m.status}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
